// CustomerLandingPage.jsx

import React, { useState } from 'react';
import { initializeApp } from 'firebase/app';
import { getFirestore, collection, addDoc, serverTimestamp, doc, getDoc, Timestamp } from 'firebase/firestore';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

const CustomerLandingPage = () => {
  const pathParts = window.location.pathname.split('/');
  const userId = pathParts[pathParts.length - 1];

  const [isLinkActive, setIsLinkActive] = useState(true);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    phone: '', 
    birthDate: '', 
    email: '',
    address: ''
  });

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleWhatsAppChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length <= 11) {
      value = value.replace(/^(\d{2})(\d{4,5})(\d{4}).*/, '($1) $2-$3');
    }
    setFormData({ ...formData, phone: value.slice(0, 15) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userId) {
      setError('ERRO DE ROTA: URL inválida, ID não detectado.');
      return;
    }

    if (!formData.name.trim() || !formData.phone.trim() || !formData.address.trim()) {
      setError('Por favor, preencha os campos obrigatórios.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      let birthDateTimestamp = null;

      // TRATAMENTO NÍVEL 10: Extremamente restritivo.
      // Só tenta converter se for EXATAMENTE o padrão YYYY-MM-DD (Android/PC).
      if (formData.birthDate && formData.birthDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = formData.birthDate.split('-').map(Number);
        const dateObj = new Date(year, month - 1, day);
        if (!isNaN(dateObj.getTime())) {
          birthDateTimestamp = Timestamp.fromDate(dateObj);
        }
      }
      // Se for o Autofill do iOS ("11 de mar. de 1988"), a variável continua null.
      // Garantimos a conversão e não perdemos o contato do lead.

      // Montagem limpa do payload para evitar dados residuais do formulário
      const payload = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        address: formData.address.trim(),
        birthDate: birthDateTimestamp,
        userId: userId,
        createdAt: serverTimestamp()
      };

      await addDoc(collection(db, 'users', userId, 'customers'), payload);

      setSuccess(true);
    } catch (err) {
      console.error("ERRO CAPTURADO:", err);
      // O PULO DO GATO: Imprime a mensagem real do Firebase na tela
      setError(`Falha ao salvar: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  React.useEffect(() => {
    const checkStatus = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          setIsLinkActive(userDoc.data().isLandingPageActive ?? true);
        }
      } catch (err) {
        console.error("Erro ao validar status:", err);
      } finally {
        setCheckingStatus(false);
      }
    };
    checkStatus();
  }, [userId]);

  if (checkingStatus) return <div className="min-h-screen bg-[#F4EFEC] flex items-center justify-center text-[#9B9A98] tracking-widest uppercase text-sm">Aguarde...</div>;

  if (!isLinkActive) {
    return (
      <div className="min-h-screen bg-[#F4EFEC] flex items-center justify-center p-6 text-center">
        <div className="bg-white p-12 rounded-sm shadow-xl max-w-md border border-[#F2DFDB]">
          <h1 className="text-xl uppercase tracking-widest text-gray-900 mb-4 font-light">⛔</h1>
          <h1 className="text-xl uppercase tracking-widest text-gray-900 mb-4 font-semibold">Acesso Indisponível</h1>
          <p className="text-gray-500 text-sm font-light">No momento, não estamos aceitando novos cadastros. Agradecemos a sua procura.</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#F4EFEC] flex items-center justify-center p-6 text-center">
        <div className="bg-white p-12 rounded-sm shadow-xl max-w-md border border-[#F2DFDB]">
          <h1 className="text-2xl uppercase tracking-widest text-gray-900 mb-4 font-light">✅</h1>
          <h1 className="text-2xl uppercase tracking-widest text-gray-900 mb-4 font-semibold">Cadastro Concluído</h1>
          <p className="text-gray-500 text-sm font-light leading-relaxed">Suas informações foram registradas com sucesso. Agradecemos o seu envio.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4EFEC] p-6 flex flex-col items-center justify-center font-sans">
      <div className="max-w-lg w-full bg-white rounded-sm shadow-2xl p-10 border border-[#F2DFDB]">
        
        <div className="text-center mb-10">
            <h1 className="text-2xl uppercase tracking-widest text-gray-900 mb-2 font-semibold">Cadastro de Cliente</h1>
            <p className="text-[#9B9A98] text-sm font-light tracking-wide">Insira suas informações para acesso exclusivo.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-gray-500 mb-2">Nome Completo *</label>
            <input type="text" name="name" required onChange={handleChange} className="w-full p-3 bg-transparent border border-[#F2DFDB] rounded-sm focus:border-[#D7BFBD] focus:ring-1 focus:ring-[#D7BFBD] outline-none transition-all text-gray-800" placeholder="Ex: Maria da Silva" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs uppercase tracking-wider font-semibold text-gray-500 mb-2">WhatsApp *</label>
                <input type="tel" name="phone" required value={formData.phone} onChange={handleWhatsAppChange} placeholder="(00) 00000-0000" className="w-full p-3 bg-transparent border border-[#F2DFDB] rounded-sm focus:border-[#D7BFBD] focus:ring-1 focus:ring-[#D7BFBD] outline-none transition-all text-gray-800" />
              </div>
        
              <div>
                <label className="block text-xs uppercase tracking-wider font-semibold text-gray-500 mb-2">Nascimento</label>
                <input type="date" name="birthDate" onChange={handleChange} className="w-full p-3 bg-transparent border border-[#F2DFDB] rounded-sm focus:border-[#D7BFBD] focus:ring-1 focus:ring-[#D7BFBD] outline-none transition-all text-gray-800" />
              </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-gray-500 mb-2">E-mail</label>
            <input type="email" name="email" onChange={handleChange} className="w-full p-3 bg-transparent border border-[#F2DFDB] rounded-sm focus:border-[#D7BFBD] focus:ring-1 focus:ring-[#D7BFBD] outline-none transition-all text-gray-800" placeholder="contato@email.com" />
          </div>
          
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-gray-500 mb-2">Endereço Completo *</label>
            <textarea name="address" required onChange={handleChange} rows="2" className="w-full p-3 bg-transparent border border-[#F2DFDB] rounded-sm focus:border-[#D7BFBD] focus:ring-1 focus:ring-[#D7BFBD] outline-none transition-all text-gray-800 resize-none" placeholder="Rua, número, complemento, bairro e cidade..."></textarea>
          </div>
          
          {error && <p className="text-red-800 bg-red-50 p-3 rounded-sm border border-red-200 text-sm text-center font-medium tracking-wide">{error}</p>}
          
          <div className="pt-4">
              <button type="submit" disabled={submitting} className="w-full bg-gray-900 text-white uppercase tracking-widest py-4 text-sm font-semibold hover:bg-gray-800 transition-colors rounded-sm shadow-md disabled:bg-gray-400">
                {submitting ? 'Processando...' : 'Finalizar Cadastro'}
              </button>
          </div>
        </form>

      </div>
    </div>
  );
};

export default CustomerLandingPage;